import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Badge } from 'react-bootstrap';
import { FiFileText } from 'react-icons/fi';
import { showSuccess, showError as showToastError } from '@infrastructure/api/utils/toastNotifications';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { ErrorResponse } from '@core/types/errorResponse';
import { useDepartments } from '@features/department/hooks/useDepartments';
import DepartmentSelectOptions from '@features/department/components/DepartmentSelectOptions';
import { MAIN_OFFICE_DEPARTMENT_ID } from '@features/department/constants/departmentIds';
import { partitionDepartmentsForSelect } from '@features/department/utils/partitionDepartmentsForSelect';
import { useReportTemplates } from '@features/report/template/hooks/useReportTemplates';
import {
  SORT_BY_DEPARTMENT_NAME,
  SORT_BY_REPORT_TEMPLATE_NAME,
  SORT_DIR_ASC,
} from '@core/constants/listSort';
import {
  bulkAssignTemplatesToChurches,
  bulkUnassignTemplatesFromChurches,
  bulkAssignTemplatesToDistricts,
  bulkUnassignTemplatesFromDistricts,
} from '@features/church/api/churchApi';
import { usePermissions } from '@features/entity/hooks/usePermissions';
import { ACTION_CODES } from '@features/entity/types/actionCodes';
import { todayDateOnly } from '@core/utils/formatDateOnly';
import AssignBulkMetadataFields from './AssignBulkMetadataFields';

interface BulkEntity {
  id: number;
  name: string;
}

interface Props {
  show: boolean;
  onHide: () => void;
  entities: BulkEntity[];
  entityType: 'church' | 'district';
  nationId: number;
  districtId?: number;
  onCompleted?: () => void;
}

const BulkAssignTemplatesModal: React.FC<Props> = ({
  show,
  onHide,
  entities,
  entityType,
  nationId,
  districtId: _districtId,
  onCompleted,
}) => {
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number>(0);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([]);
  const [action, setAction] = useState<'assign' | 'unassign'>('assign');
  const [processing, setProcessing] = useState(false);
  const [isRequired, setIsRequired] = useState(true);
  const [requiredAt, setRequiredAt] = useState(() => todayDateOnly());
  const [overrideExisting, setOverrideExisting] = useState(false);

  const showError = useApiError();

  const overrideCode =
    entityType === 'church'
      ? ACTION_CODES.BULK_ASSIGN_OVERRIDE_CHURCH_REPORT_TEMPLATE
      : ACTION_CODES.BULK_ASSIGN_OVERRIDE_DISTRICT_REPORT_TEMPLATE;

  const { hasPermission } = usePermissions({
    scopeType: 'NATION',
    scopeId: nationId,
    actionCodes: [overrideCode],
  });
  const canOverride = hasPermission(overrideCode);

  const { departments, loading: loadingDepartments } = useDepartments({
    all: true,
    isActive: true,
    nationId,
    sortBy: SORT_BY_DEPARTMENT_NAME,
    sortDir: SORT_DIR_ASC,
  });

  const originScope = entityType === 'church' ? 'CHURCH' : 'DISTRICT';
  const originLabel = entityType === 'church' ? 'church' : 'district';

  const { data: templates, loading: loadingTemplates } = useReportTemplates({
    departmentId: selectedDepartmentId,
    originScope,
    all: true,
    isActive: true,
    sortBy: SORT_BY_REPORT_TEMPLATE_NAME,
    sortDir: SORT_DIR_ASC,
  });

  useEffect(() => {
    if (departments.length > 0 && selectedDepartmentId === 0) {
      const { mainOffice, others } = partitionDepartmentsForSelect(departments);
      const defaultId =
        mainOffice?.department_id ??
        others[0]?.department_id ??
        MAIN_OFFICE_DEPARTMENT_ID;
      setSelectedDepartmentId(defaultId);
    }
  }, [departments, selectedDepartmentId]);

  useEffect(() => {
    setSelectedTemplateIds([]);
    setIsRequired(true);
    setRequiredAt(todayDateOnly());
    setOverrideExisting(false);
  }, [selectedDepartmentId, show]);

  const handleTemplateToggle = (templateId: number) => {
    setSelectedTemplateIds((prev) =>
      prev.includes(templateId)
        ? prev.filter((id) => id !== templateId)
        : [...prev, templateId]
    );
  };

  const handleSubmit = async () => {
    if (!selectedDepartmentId || selectedTemplateIds.length === 0) return;

    setProcessing(true);

    try {
      const entityIds = entities.map((e) => e.id);
      const assignOptions = {
        isRequired,
        requiredAt: isRequired && requiredAt ? requiredAt : null,
        overrideExisting: canOverride ? overrideExisting : false,
      };

      let response;
      if (entityType === 'church') {
        if (action === 'assign') {
          response = await bulkAssignTemplatesToChurches(
            nationId,
            selectedDepartmentId,
            entityIds,
            selectedTemplateIds,
            assignOptions
          );
        } else {
          response = await bulkUnassignTemplatesFromChurches(
            nationId,
            entityIds,
            selectedTemplateIds
          );
        }
      } else {
        if (action === 'assign') {
          response = await bulkAssignTemplatesToDistricts(
            nationId,
            selectedDepartmentId,
            entityIds,
            selectedTemplateIds,
            assignOptions
          );
        } else {
          response = await bulkUnassignTemplatesFromDistricts(
            nationId,
            entityIds,
            selectedTemplateIds
          );
        }
      }

      if (response.isSuccess()) {
        const data = response.getData();

        if (action === 'assign') {
          const assignedCount = data?.assigned?.length ?? 0;
          const updatedCount = data?.updated?.length ?? 0;
          const skipCount =
            (data?.skipped_already?.length ?? 0) + (data?.skipped_inactive?.length ?? 0);
          const failCount = data?.failed?.length ?? 0;

          if (assignedCount > 0) {
            showSuccess(
              `Assigned ${assignedCount} template${assignedCount !== 1 ? 's' : ''} across selected ${entityType}${entities.length !== 1 ? (entityType === 'church' ? 'es' : 's') : ''}.`
            );
          }
          if (updatedCount > 0) {
            showSuccess(
              `Updated ${updatedCount} existing assignment${updatedCount !== 1 ? 's' : ''} with new required settings.`
            );
          }
          if (skipCount > 0) {
            showToastError(
              `${skipCount} assignment${skipCount !== 1 ? 's were' : ' was'} skipped (already assigned or inactive entity).`
            );
          }
          if (failCount > 0) {
            showToastError(
              `Failed to assign ${failCount} template assignment${failCount !== 1 ? 's' : ''}.`
            );
          }
          if (assignedCount === 0 && updatedCount === 0 && skipCount === 0 && failCount === 0) {
            showSuccess(`No changes were needed for the selected ${entityType}s.`);
          }
        } else {
          const successCount = data?.unassigned?.length ?? 0;
          const skipCount =
            (data?.skipped_not_assigned?.length ?? 0) + (data?.skipped_inactive?.length ?? 0);
          const failCount = data?.failed?.length ?? 0;

          if (successCount > 0) {
            showSuccess(
              `Successfully unassigned ${successCount} template${successCount !== 1 ? 's' : ''} across selected ${entityType}${entities.length !== 1 ? (entityType === 'church' ? 'es' : 's') : ''}.`
            );
          }
          if (skipCount > 0) {
            showToastError(
              `${skipCount} assignment${skipCount !== 1 ? 's were' : ' was'} skipped (not assigned or inactive entity).`
            );
          }
          if (failCount > 0) {
            showToastError(
              `Failed to unassign ${failCount} template assignment${failCount !== 1 ? 's' : ''}.`
            );
          }
          if (successCount === 0 && skipCount === 0 && failCount === 0) {
            showSuccess(`No changes were needed for the selected ${entityType}s.`);
          }
        }

        onHide();
        onCompleted?.();
      } else {
        showError(response.getError() as ErrorResponse);
      }
    } catch (error) {
      showError(error as ErrorResponse);
    } finally {
      setProcessing(false);
    }
  };

  const isLoading = loadingDepartments || loadingTemplates;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <FiFileText aria-hidden />
          Bulk {action === 'assign' ? 'Assign' : 'Unassign'} Templates
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <Badge bg="secondary" className="me-2">
            {entities.length} {entityType}{entities.length !== 1 ? (entityType === 'church' ? 'es' : 's') : ''} selected
          </Badge>
          <Badge bg="light" text="dark" className="border">
            {originLabel} origin
          </Badge>
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold small">Action</Form.Label>
          <div className="d-flex gap-3">
            <Form.Check
              type="radio"
              id="action-assign"
              label="Assign templates"
              checked={action === 'assign'}
              onChange={() => setAction('assign')}
            />
            <Form.Check
              type="radio"
              id="action-unassign"
              label="Unassign templates"
              checked={action === 'unassign'}
              onChange={() => setAction('unassign')}
            />
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold small">Department</Form.Label>
          <Form.Select
            size="sm"
            value={selectedDepartmentId}
            onChange={(e) => setSelectedDepartmentId(Number(e.target.value))}
            disabled={loadingDepartments || processing}
          >
            {loadingDepartments && <option value={0}>Loading departments...</option>}
            {!loadingDepartments && departments.length === 0 && (
              <option value={0}>No departments found</option>
            )}
            <DepartmentSelectOptions departments={departments} />
          </Form.Select>
        </Form.Group>

        {action === 'assign' && (
          <AssignBulkMetadataFields
            isRequired={isRequired}
            onIsRequiredChange={setIsRequired}
            requiredAt={requiredAt}
            onRequiredAtChange={setRequiredAt}
            overrideExisting={overrideExisting}
            onOverrideExistingChange={setOverrideExisting}
            showOverrideToggle={canOverride}
            disabled={processing}
          />
        )}

        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : !selectedDepartmentId ? (
          <div className="text-muted text-center py-4">
            Select a department to see available templates.
          </div>
        ) : (templates ?? []).length === 0 ? (
          <div className="text-muted text-center py-4">
            No {originLabel}-origin templates available for this department.
          </div>
        ) : (
          <div className="border rounded p-3" style={{ maxHeight: 300, overflow: 'auto' }}>
            <div className="fw-semibold small mb-2">
              Select {originLabel}-origin templates to {action}
            </div>
            {(templates ?? []).map((template) => (
              <Form.Check
                key={template.report_template_id}
                type="checkbox"
                id={`template-${template.report_template_id}`}
                label={template.report_template_name}
                checked={selectedTemplateIds.includes(template.report_template_id)}
                onChange={() => handleTemplateToggle(template.report_template_id)}
                disabled={processing}
                className="mb-2"
              />
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={processing}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={processing || selectedTemplateIds.length === 0 || !selectedDepartmentId}
        >
          {processing ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Processing...
            </>
          ) : (
            `${action === 'assign' ? 'Assign' : 'Unassign'} to ${entities.length} ${entityType}${entities.length !== 1 ? (entityType === 'church' ? 'es' : 's') : ''}`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkAssignTemplatesModal;
